import { useDistance } from '@/composables/useDistance.ts';
import { useManyCubes } from '@/composables/useManyCubes.ts';
import { Loader, type Library } from '@googlemaps/js-api-loader';
import { useAppStore } from '@/stores/useAppStore';
import { useCubeStore } from '@/stores/useCubeStore';
import { useMapStore } from '@/stores/useMapStore';
import debounce from 'debounce';
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';

// We need these variables to live outside the composable function for them to
// globally available (behaving like static variables) when useMap is called
// from separate pieces of code.

// Note that mapObject is not reactive. When making it reactive the markers
// did not render.
let mapObject = shallowRef(null)
let google = shallowRef(null)
let loader = null;

// MapVisible reflects whether the app started showing the map.
const mapVisible = ref(false)
// MapInitialized gets set to true when the map is visible and done initializing.
// See onMapInitialized().
const mapInitialized = ref(false)

export function useMap(
  router,
  rawCubes,
  filteredCubes,
  isTour = false,
) {
  const appStore = useAppStore()
  const mapStore = useMapStore()
  const { hasProduction, cubeIsClickable } = useManyCubes()
  const { getDistance } = useDistance()

  const mapRef = ref(null)
  const markerRefs = ref([])
  const infoWindow = ref(null)
  const zoomControlOptions = ref(null)
  const zoomLevel = ref(isTour ? mapStore.defaultZoomTour : mapStore.defaultZoom)
  const showMarkerLabelsZoomThreshold = ref(isTour ? 7 : 13)

  const googleMapsApiLoaded = ref(false)
  const maxNumMarkersVisible = ref(100)

  const doOpenInfoWindowInUpdateMarkersIfNecessary = ref(true)
  const visibleMarkers = ref([])
  const allMarkers = ref({})
  const selectedCubeName = ref(router.currentRoute.value.params.selected)
  const cubesLoaded = ref(rawCubes.value.length > 0)
  const renderInfoWindowComponent = ref(false)

  // The mapCenter can't be computed since the user can pan the map which changes
  // the center coordinates independently of the url / the currently selected cube.
  const mapCenter = ref({
    lat: useCubeStore().latitude,
    lng: useCubeStore().longitude,
  })

  /////////////////////////////////////////////////////////////////////////////
  // COMPUTED
  /////////////////////////////////////////////////////////////////////////////

  const numVisibleMarkers = computed(() => visibleMarkers.value.length)

  const showMarkerLabels = computed(() => {
     return zoomLevel.value > showMarkerLabelsZoomThreshold.value
  })

  /////////////////////////////////////////////////////////////////////////////
  // METHODS / FUNCTIONS
  /////////////////////////////////////////////////////////////////////////////

  const initializeMap = async() => {
    const mapsLibraries: Library[] = ['marker']
    if (isTour) {
      mapsLibraries.push('geometry')
    }

    if (!loader) {
      loader = new Loader({
        apiKey: appStore.googleMapsApiKey,
        version: 'weekly',
        libraries: mapsLibraries,
      });
    }

    const googleObject = await loader.load();
    google.value = googleObject
    zoomControlOptions.value = {
      position: google.value.maps.ControlPosition.LEFT_BOTTOM,
    }
    googleMapsApiLoaded.value = true
    updateMaxNumMarkersVisible()
    showMapIfReady()
  }

  const showMarker = (cube) => {
    let marker = getMarkerForCubeName(cube.cubeName)
    if (!marker) {
      marker = createAndAddMarker(cube, new google.value.maps.LatLng(
        cube.params.latitude,
        cube.params.longitude,
      ))
      allMarkers.value[cube.cubeName] = marker
    } else if (!marker.map) {
      marker.map = mapObject.value
    }
  }

  const hideMarker = (cube) => {
    allMarkers.value[cube.cubeName].map = null
  }

  const updateMapMarkers = () => {
    if (
      !googleMapsApiLoaded.value ||
      !cubesLoaded.value ||
      !mapInitialized.value
    ) {
      return
    }

    const mapBounds = mapObject.value.getBounds()
    const newVisibleMarkers = []

    rawCubes.value
      .map((cube) => {
        // Add the distance to the current map center.
        cube.distanceToCenter = getDistance(mapCenter.value, {
          lat: cube.params.latitude,
          lng: cube.params.longitude,
        })
        return cube
      })
      // Sort cubes by distance from the center.
      .sort((a, b) => a.distanceToCenter - b.distanceToCenter)
      .map((cube) => {
        const cubeName = cube.cubeName
        const marker = getMarkerForCubeName(cubeName)
        let markerPosition
        if (!marker) {
          markerPosition = new google.value.maps.LatLng(
            cube.params.latitude,
            cube.params.longitude,
          )
        } else {
          markerPosition = marker.position
        }

        // Show the marker if it's within the visible map bounds and if the limit
        // of visible cubes is not reached yet.
        const markerInVisibleMapArea = mapBounds.contains(markerPosition)
        const markerShouldBeVisible =
          isTour ||
          (
            cube.showInFilteredList &&
            newVisibleMarkers.length < maxNumMarkersVisible.value &&
            markerInVisibleMapArea &&
            // Hide consumers when zooming out to not fill the map with
            // overlapping consumer cubes.
            (
              filteredCubes.value.length < maxNumMarkersVisible.value ||
              zoomLevel.value > 10 ||
              cube.hasProduction
            )
          )

        if (markerShouldBeVisible) {
          showMarker(cube)
          newVisibleMarkers.push(allMarkers.value[cubeName])

          if (
            selectedCubeName.value &&
            selectedCubeName.value === cube.cubeName &&
            doOpenInfoWindowInUpdateMarkersIfNecessary.value &&
            cubeIsClickable(cube)
          ) {
            openInfoWindow(cube.cubeName)
          }
        }
        else if (marker) {
          hideMarker(cube)
        }
      })

    visibleMarkers.value = newVisibleMarkers
    doOpenInfoWindowInUpdateMarkersIfNecessary.value = false
  }

  const getCubeMarkerLabel = (rawCube) => {
    const cube = rawCube.params
    const isMe = cube.nickName === useCubeStore().nickName
    let result = cube.nickName

    if (rawCube.hasProduction) {
      if (cube.openConnectionsAvailable && cube.openConnectionsAvailable > 0) {
        result += ` (${cube.openConnectionsAvailable})`
      }
      return result
    } else if (
      isMe ||
      rawCube.canReceiveDonations ||
      cube.infoWindowText ||
      (cube.infoWindowImageDetails && cube.infoWindowImageDetails.original)
    ) {
      return result
    } else {
      return ''
    }
  }

  const createAndAddMarker = (cube, markerPosition) => {
    const img = document.createElement('img');
    img.src = mapStore.cubeIconUrl(cube, 'svg')
    const wrapper = document.createElement('div')
    wrapper.classList.add('map-marker', 'map-marker--' + getCubeMarkerType(cube))
    wrapper.setAttribute('title', cube.params.nickName ?? cube.cubeName)
    wrapper.appendChild(img)
    const labelEl = document.createElement('div')
    labelEl.classList.add('map-marker__label')
    labelEl.innerText = getCubeMarkerLabel(cube)
    wrapper.appendChild(labelEl)

    const marker = new google.value.maps.marker.AdvancedMarkerElement({
      map: mapObject.value,
      position: markerPosition,
      zIndex: hasProduction(cube) || cube.canReceiveDonations ? 10 : 1,
      title: cube.params.nickName,
      content: wrapper,
    })

    marker.addListener('gmp-click', () => {
      if (cubeIsClickable(cube)) {
        if (selectedCubeName.value === cube.cubeName) {
          openInfoWindow(cube.cubeName)
        } else {
          router.push({
            name: isTour ? 'tour' : 'map',
            params: { selected: cube.cubeName },
          })
        }
      }
    })
    return marker
  }

  const showMapIfReady = () => {
    if (mapVisible.value || !googleMapsApiLoaded.value) {
      return
    }

    mapObject.value = new google.value.maps.Map(mapRef.value, {
      center: new google.value.maps.LatLng(mapCenter.value.lat, mapCenter.value.lng),
      zoom: zoomLevel.value,
      minZoom: 4,
      maxZoom: 21,
      zoomControlOptions: {
        position: google.value.maps.ControlPosition.LEFT_BOTTOM,
      },
      streetViewControl: false,
      fullscreenControl: false,
      gestureHandling: 'greedy',
      mapTypeControlOptions: {
        mapTypeIds: [],
      },
      mapId: appStore.googleMapsMapId,
    })

    mapObject.value.addListener('center_changed', onMapCenterChanged)
    mapObject.value.addListener('zoom_changed', onMapZoomLevelChanged)
    // Since there is no map ready / initialized event, we use the idle event
    // as suggested at https://stackoverflow.com/questions/832692/how-can-i-check-whether-google-maps-is-fully-loaded
    // Unfortunately the idle event is fired before the map is finished initializing.
    // To work around this, we also listen to the tilesloaded event.
    google.value.maps.event.addListenerOnce(mapObject.value, 'idle', () => {
      if (mapObject.value.getRenderingType() !== 'UNINITIALIZED') {
        onMapInitialized()
      }
    })
    google.value.maps.event.addListenerOnce(mapObject.value, 'tilesloaded', () => {
      if (mapObject.value.getRenderingType() !== 'UNINITIALIZED') {
        onMapInitialized()
      }
    })

    infoWindow.value = new google.value.maps.InfoWindow({
      // We create an html tag which will be used as target for <Teleport> in
      // MapsPopup.vue to render content to.
      content: '<div id="ef-map-info-window-content"></div>',
    })

    mapVisible.value = true

    // We need to pan to the selected cube if we have received the list of cubes
    // already because panning to the cube happens when receiving the cubes list
    // which happened before showing the map in this case and whe the route
    // parameter 'selected' has been changed.
    // So if the app was loaded, the user started out in another page and then
    // navigates to the map, for example by clicking on a producer on the efriends
    // page, we need to pan to the cube here because otherwise it might be outside
    // of the visible bounds around the users home address and the infowindow would
    // not be shown.
    if (selectedCubeName.value && cubesLoaded.value) {
      panToCubeName(selectedCubeName.value)
    }
    updateMapMarkers()
  }

  /**
   * Call this method to adjust zoom + pan to fit the currently displayed markers.
   */
  const zoomToFitResults = () => {
    const markerBounds = new google.value.maps.LatLngBounds()
    if (!mapObject.value || !numVisibleMarkers.value) {
      return
    }

    filteredCubes.value
      .map((cube) => {
        // Add the distance to the current map center.
        cube.distanceToCenter = getDistance(mapCenter.value, {
          lat: cube.params.latitude,
          lng: cube.params.longitude,
        })
        return cube
      })
      .sort((a, b) => a.distanceToCenter - b.distanceToCenter)
      .filter((cube, index) => {
        return index < maxNumMarkersVisible.value
      })
      // Note that we're not using .filter to only call extend later in .map
      // to prevent a second time looping over the (filtered) cubes. So this
      // should perform a little bit better.
      .map((cube, index) => {
        if (index < maxNumMarkersVisible.value) {
          const marker = getMarkerForCubeName(cube.cubeName)
          if (!marker) {
            throw new Error('Could not find marker for cube ' + cube.cubeName + '.')
          }
          markerBounds.extend(marker.position)
        }
      })

    mapObject.value.fitBounds(markerBounds)
  }

  const openInfoWindow = async (cubeName) => {
    const marker = getMarkerForCubeName(cubeName)
    if (!marker) {
      throw new Error('openInfoWindow(): Could not find marker for cube to select / show: ' + cubeName)
    }
    // Make sure the current route is available in MapsPopup which needs to
    // check if we're in the tour by checking the route.
    await router.isReady()
    infoWindow.value.open(mapRef.value, marker)
    // Since the maps popup is rendered via <Teleport> into the map, we need to
    // make sure the element with the id #ef-map-info-window-content exists in
    // the dom, which might take a bit until google's info window is actually
    // rendered.
    setTimeout(() => {
      renderInfoWindowComponent.value = true
    }, 100)
  }

  function panToCubeName(cubeName) {
    const cube = mapStore.getCubeByName(cubeName, isTour ? 'tour' : 'global')
    if (
      cube &&
      'params' in cube &&
      'latitude' in cube.params &&
      'longitude' in cube.params
    ) {
      mapObject.value.panTo(
        new google.value.maps.LatLng(cube.params.latitude, cube.params.longitude)
      )
    }
  }

  const getCubeMarkerType = (cube) => {
    if (mapStore.isEfriendOfTheMonth(cube)) {
      return 'special';
    } else if (hasProduction(cube)) {
      return 'producer';
    } else {
      return 'consumer';
    }
  }

  const getMarkerForCubeName = (cubeName) => {
    return allMarkers.value[cubeName] ?? false
  }

  const updateMaxNumMarkersVisible = () => {
    if (!mapRef.value) {
      return
    }

    const rect = mapRef.value.getBoundingClientRect()
    const area = rect.width * rect.height
    // This is a rough estimation (=magic number) based on trying out values.
    let result = Math.round(area / 4000)

    if (result < 100) {
      result = 100
    } else if (result > 500) {
      result = 500
    }

    maxNumMarkersVisible.value = result
  }

  /////////////////////////////////////////////////////////////////////////////
  // EVENTS
  /////////////////////////////////////////////////////////////////////////////

  onMounted(() => {
    window.addEventListener('resize', updateMaxNumMarkersVisible)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', updateMaxNumMarkersVisible)
    mapVisible.value = false
    mapInitialized.value = false
  })

  const onSelectedCubeRouteParamChanged = (cubeName) => {
    doOpenInfoWindowInUpdateMarkersIfNecessary.value = true
    selectedCubeName.value = cubeName
    panToCubeName(cubeName)
  }

  const onCubesListReceived = (newRawCubes, oldRawCubes) => {
    if (oldRawCubes.length < 1) {
      cubesLoaded.value = true

      if (selectedCubeName.value) {
        panToCubeName(selectedCubeName.value)
      }
    }
  }

  const onCubeFilterChanged = () => {
    updateMapMarkers()
  }

  const onMapInitialized = () => {
    mapInitialized.value = true
    updateMapMarkers()
  }

  const onMapCenterChanged = debounce(() => {
    const rawCenter = mapObject.value.getCenter()
    mapCenter.value = {
      lat: rawCenter.lat(),
      lng: rawCenter.lng(),
    }
    updateMapMarkers()
  }, 300)

  const onMapZoomLevelChanged = () => {
    zoomLevel.value = mapObject.value.getZoom()
  }


  /////////////////////////////////////////////////////////////////////////////
  // WATCHERS
  /////////////////////////////////////////////////////////////////////////////
  watch(
    () => router.currentRoute.value.params.selected,
    onSelectedCubeRouteParamChanged,
  )

  watch(rawCubes, onCubesListReceived)
  watch(filteredCubes, onCubeFilterChanged)


  /////////////////////////////////////////////////////////////////////////////
  // INITIALIZATION
  /////////////////////////////////////////////////////////////////////////////
  initializeMap()


  return {
    mapRef,
    mapObject,
    mapInitialized,
    google,
    markerRefs,
    zoomToFitResults,
    zoomLevel,
    zoomControlOptions,
    selectedCubeName,
    numVisibleMarkers,
    infoWindow,
    renderInfoWindowComponent,
    updateMapMarkers,
    showMarkerLabels,
  }
}